import { useInfiniteQuery } from '@tanstack/react-query';

import { voiceApi } from '@/api/services/voice.api';
import { voiceKeys } from '../query-keys';

const HISTORY_PAGE_SIZE = 20;

interface UseCallHistoryInput {
    /** Filter to one line (the selected caller ID); undefined = all org calls. */
    readonly channelIdentityId?: string;
    /** Endpoint requires VOICE_CALLS_READ — gate so the query never 403s. */
    readonly enabled?: boolean;
}

/**
 * Infinite call history for the widget's History tab — newest first, all
 * teammates' calls on the selected number (or the whole org on "Auto").
 */
export function useCallHistory({ channelIdentityId, enabled }: UseCallHistoryInput) {
    return useInfiniteQuery({
        queryKey: voiceKeys.calls.history(channelIdentityId ?? null),
        queryFn: ({ pageParam }) =>
            voiceApi.listCalls({
                ...(channelIdentityId ? { channelIdentityId } : {}),
                // The softphone widget is the team's dialer: its history stays team calls.
                handledBy: 'team',
                limit: HISTORY_PAGE_SIZE,
                page: pageParam,
                sortDir: 'desc',
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
        enabled: enabled ?? true,
        staleTime: 15_000,
    });
}

interface UseCounterpartHistoryInput {
    /** Exact E.164 of the other party. */
    readonly counterpartNumber: string;
    /** Endpoint requires VOICE_CALLS_READ — gate so the query never 403s. */
    readonly enabled?: boolean;
}

/**
 * Infinite per-number call timeline for the widget's History detail view —
 * every call with this counterpart across ALL org lines (the caller-ID
 * filter is a History-list concern), newest first, with the lite recordings
 * projection for inline playback.
 */
export function useCounterpartCallHistory({
    counterpartNumber,
    enabled,
}: UseCounterpartHistoryInput) {
    return useInfiniteQuery({
        queryKey: voiceKeys.calls.counterpart(counterpartNumber),
        queryFn: ({ pageParam }) =>
            voiceApi.listCalls({
                counterpartNumber,
                includeRecordings: true,
                handledBy: 'team',
                limit: HISTORY_PAGE_SIZE,
                page: pageParam,
                sortDir: 'desc',
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
        enabled: enabled ?? true,
        staleTime: 15_000,
    });
}
